import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { MESSAGES } from "~/resources/Message";
import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { UseFormCloudEnvironment } from "./UseFormCloudEnvironment";
import { getProviders } from "~/Utils/Utils";
import { REGEX } from "~/resources/Regex";
import { SIMPLBreadcrumb } from "../../components/commons/breadcrumb/SimplBreadCrumb";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { useLocation, useNavigate } from "react-router-dom";
import { FormCloudEnvironment } from "~/models/Commons ";
import { useGetCloudEnvironmentsQuery } from "~/services/cloudenvironment";
import { useTranslation } from "react-i18next";

export const EditCloudEnvironment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { refetch } = useGetCloudEnvironmentsQuery({
    page: 0,
    size: 999,
    active: true,
  });
 
  const editedItem = location.state?.editedItem as FormCloudEnvironment | null;
  const {
    handleSubmit,
    control,
    errors,
    isValid,
    isSubmitting,
    updateForm,
    setValue,
    provider,
    isLoading,
    isError,
  } = UseFormCloudEnvironment(refetch, editedItem?.id);
  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);
  const fetchProviderList = () => {
    setCloudProviderList(getProviders(provider));
  };
  useEffect(() => {
    fetchProviderList();
  }, [provider]);

  useEffect(() => {
    if (editedItem?.id) {
      setValue("environmentName", editedItem.environmentName);
      setValue("environmentDescription", editedItem.environmentDescription);
      setValue("datacenterName", editedItem.datacenterName);
      setValue("datacenterDescription", editedItem.datacenterDescription);
      setValue("location", editedItem.location);
      setValue("iac", editedItem.iac);
      setValue(
        "authenticationConfig",
        editedItem.authenticationConfig
          ? JSON.stringify(editedItem.authenticationConfig, null, 2)
          : "",
      );
      const formCloudEnvironmentResponse: any = editedItem;
      const providers = getProviders(provider);
      const selectedProvider: any = providers?.find(
        (p) => p.id === formCloudEnvironmentResponse!.cloudProvider.id,
      );

      if (selectedProvider) {
        setValue("cloudProviderId", {
          label: selectedProvider.cloudProviderName,
          value: selectedProvider.id,
        });
      }
    }
  }, [editedItem, setValue, provider]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    handleSubmit(updateForm)(event);
  };
  const handleCancel = () => {
    navigate("/cloud-environment");
  };
  return (
    <SIMPLMenuHeader>
      {/* PAGE CONTAINER */}
      <LoadingWrapper
        loadingTitle="Loading cloudEnvironment..."
        isLoading={isLoading}
        isError={isError}
        testId="cloud-env-edit-loading"
      >
        <div className="flex justify-center bg-white">
          {/* CARD */}
          <div className="w-full max-w-[1104px] rounded-xl border border-[#D6D6D6] bg-white">
            {/* BREADCRUMB + TITLE */}
            <div className="pt-4 px-4">
              <SIMPLBreadcrumb />
              <h1 className="mt-2 font-['Arial'] text-[28px] font-bold leading-[44px] text-left text-Foundation/Heading normal-case">
                {t("CloudEnvironment")}
              </h1>
            </div>

            <form onSubmit={handleFormSubmit}>
              {/* FORM CONTENT */}
              <div className="pt-4 space-y-2 px-4 pb-4">
                <SIMPLInput
                  name="environmentName"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('MessageNameRequired'),
                    },
                    pattern: {
                      value: REGEX.EnvironmentNameRegex,
                      message: MESSAGES.MessageRegexEnvironmentName,
                    },
                    messageRequired: t('MessageNameRequired'),
                    maxLength: {
                      value: 63,
                      message: t('NameTooLong'),
                    },
                  }}
                  label={t("Name")}
                  placeholder={t("Name")}
                  required
                  id="environmentName"
                  background="#F5F5F5"
                  fontWeight="bold"
                  errorLabel={errors.environmentName?.message}
                  testId="cloud-env-edit-name"
                />

                <SIMPLInput
                  name="environmentDescription"
                  control={control}
                  label={t("Description")}
                  rules={{
                    pattern: {
                      value: REGEX.CloudEnvDescriptions,
                      message: MESSAGES.MessageRegexEnvironmentDescriptions,
                    },
                  }}
                  
                  id="environmentDescription"
                  fontWeight="bold"
                  background="#F5F5F5"
                  errorLabel={errors.environmentDescription?.message}
                  placeholder={t("Description")}
                  testId="cloud-env-edit-descriptions"
                />

                <SIMPLInput
                  name="datacenterName"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('MessageDataCenterNameRequired'),
                    },
                  }}
                  messageRequired={t('MessageDataCenterNameRequired')}
                  label={t("DataCenterName")}
                  placeholder={t("DataCenterName")}
                  required
                  id="datacenterName"
                  background="#F5F5F5"
                  fontWeight="bold"
                  errorLabel={errors.datacenterName?.message}
                  testId="cloud-env-edit-datacenter-name"
                />

                <SIMPLInput
                  name="datacenterDescription"
                  control={control}
                  label={t("DataCenterDescription")}
                  placeholder={t("DataCenterDescription")}
                  rules={{
                    pattern: {
                      value: REGEX.CloudEnvDescriptions,
                      message: MESSAGES.MessageRegexEnvironmentDescriptions,
                    },
                  }}
                  id="datacenterDescription"
                  fontWeight="bold"
                  background="#F5F5F5"
                  errorLabel={errors.datacenterDescription?.message}
                  testId="cloud-env-edit-datacenter-descriptions"
                />
                <div className="grid gap-6 md:grid-cols-2 ">
                  <SIMPLInput
                    name="location"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t('MessageLocationRequired'),
                      },
                    }}
                    label={t("Location")}
                    required
                    id="location"
                    fontWeight="bold"
                    background="#F5F5F5"
                    errorLabel={errors.location?.message}
                    placeholder={t("Location")}
                    testId="cloud-env-edit-location"
                  />

                  <SIMPLInput
                    name="iac"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t('MessageIacRequired'),
                      },
                    }}
                    label={t("Iac")}
                    required
                    id="iac"
                    fontWeight="bold"
                    background="#F5F5F5"
                    errorLabel={errors.iac?.message}
                    placeholder={t("Iac")}
                    testId="cloud-env-edit-iac"
                  />

                  {/* Cloud Provider dropdown */}
                  <Controller
                    name="cloudProviderId"
                    control={control}
                    rules={{ required: t('MessageCloudProviderRequired') }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudProvider"
                        label={t("CloudProvider")}    
                        options={cloudProviderList?.map((cp) => ({
                          label: cp.cloudProviderName,
                          value: cp.id,
                        }))}
                        isRequired
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel={false}
                        errorLabel={errors.cloudProviderId?.message as string}
                        placeholder={t("CloudProviderWithSpace")}
                        testId="cloud-env-edit-cloud-provider"
                      />
                    )}
                  />
                </div>
                <SIMPLInput
                  name="authenticationConfig"
                  type="textarea"
                  rows={4}
                  control={control}
                  label={t("AuthenConfig")}
                  id="authenticationConfig"
                  fontWeight="bold"
                  background="#F5F5F5"
                  rules={{
                    validate: (value: string) => {
                      if (!value?.trim()) return true;
                      try {
                        JSON.parse(value);
                        return true;
                      } catch {
                        return t("InvalidJsonFormat");
                      }
                    },
                  }}
                  errorLabel={errors.authenticationConfig?.message}
                  placeholder={t("JsonAlreadyExits")}
                  testId="cloud-env-edit-auth-config"
                />
              </div>

              {/* ACTION BUTTONS – FULL WIDTH */}
              <div className="w-full flex justify-end gap-3 bg-[#F5F5F5] border-t border-[#D6D6D6] rounded-b-xl px-4 py-3">
                <SIMPLButtonCustomized
                  label={t("Cancel")}
                  secondary
                  buttonHandlerAction={handleCancel}
                  testId="cloud-env-edit-button-cancel"
                />
                <SIMPLButtonCustomized
                  label={t("Submit")}
                  primary
                  disabled={!isValid || isSubmitting}
                  testId="cloud-env-edit-button-submit"
                />
              </div>
            </form>
          </div>
        </div>
      </LoadingWrapper>
    </SIMPLMenuHeader>
  );
};